import React from 'react';
import moment from 'moment';
import { FiCheck, FiClipboard } from 'react-icons/fi';
import { getCollectionCount } from '../../utils/getCollectionCount';

const isImage = (url) =>
    url && /(jpeg|jpg|gif|png|webp)(\?|$)/i.test(url);

const isVideo = (url) =>
    url && /(mp4|webm|ogg|mov)(\?|$)/i.test(url);

const getVideoType = (url) => {
    const match = url.match(/\.(mp4|webm|ogg|mov)/i);
    if (!match) return 'video/mp4';
    return match[1] === 'mov' ? 'video/quicktime' : `video/${match[1]}`;
};

const copyToClipboard = async (value) => {
    if (!value || typeof value !== 'string') {
        return false;
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(value);
            return true;
        } catch {
            // Fall back below for older browsers or blocked clipboard API.
        }
    }

    if (typeof document === 'undefined') {
        return false;
    }

    try {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        return copied;
    } catch {
        return false;
    }
};

const TableBody = ({ tableData, columns, path }) => {
    const [copiedCellKey, setCopiedCellKey] = React.useState('');

    const handleCopyPress = async (value, cellKey) => {
        const copied = await copyToClipboard(value);
        if (!copied) {
            return;
        }

        setCopiedCellKey(cellKey);
        setTimeout(() => {
            setCopiedCellKey((currentKey) => (currentKey === cellKey ? '' : currentKey));
        }, 1300);
    };

    return (
        <tbody>
            {tableData.map((data) => {
                const rowClassName = path === 'users' && data.isPremiumSubscriberActive
                    ? 'table-row-premium-subscriber'
                    : '';

                return (
                    <tr key={data.id} className={rowClassName}>
                        {columns.map(({ accessor, date, label, link }) => {
                        let rawValue = data[accessor];
                        let tData = rawValue ?? "X";

                        // ✅ Convert Firebase Timestamp to formatted string
                        if (date && typeof tData === 'object' && tData?.seconds) {
                            tData = moment().diff(moment.unix(tData.seconds), 'days') < 2
                                ? moment.unix(tData.seconds).calendar()
                                : moment.unix(tData.seconds).format('MMMM Do YYYY, h:mm a');
                        }

                        let cellContent;

                        if (path === 'users' && (accessor === 'email' || accessor === 'id')) {
                            const valueToCopy = typeof rawValue === 'string' ? rawValue : '';
                            const cellKey = `${data.id}-${accessor}`;
                            const isCopied = copiedCellKey === cellKey;

                            cellContent = (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', maxWidth: '100%' }}>
                                    <span
                                        style={{
                                            overflowWrap: 'anywhere',
                                            fontSize: accessor === 'id' ? '11px' : '12px',
                                            lineHeight: 1.25,
                                        }}
                                    >
                                        {tData}
                                    </span>
                                    {valueToCopy ? (
                                        <button
                                            type="button"
                                            onClick={() => handleCopyPress(valueToCopy, cellKey)}
                                            title={isCopied ? `${label} copied` : `Copy ${label}`}
                                            aria-label={isCopied ? `${label} copied` : `Copy ${label}`}
                                            style={{
                                                width: '24px',
                                                height: '24px',
                                                padding: '0',
                                                borderRadius: '7px',
                                                border: isCopied ? '1px solid #43a047' : '1px solid var(--color-border)',
                                                background: isCopied
                                                    ? 'rgba(67, 160, 71, 0.14)'
                                                    : 'color-mix(in srgb, var(--color-surface) 88%, transparent)',
                                                color: isCopied ? '#43a047' : 'var(--color-text)',
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                transition: 'all 0.16s ease',
                                            }}
                                        >
                                            {isCopied ? <FiCheck size={13} /> : <FiClipboard size={13} />}
                                        </button>
                                    ) : null}
                                </span>
                            );
                        } else if (label === "Edit") {
                            cellContent = <a href={`/${path}/${data.id}`}>Edit</a>;
                        } else if (label === "Likes" || label === "Comments" || label === "Total Loads" || label === "Total Views") {
                            cellContent = getCollectionCount(rawValue).toLocaleString();
                        } else if (label === "Photo" && typeof tData === 'string' && isImage(tData)) {
                            cellContent = (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                    <img
                                        src={tData}
                                        alt={accessor}
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            objectFit: 'cover',
                                            borderRadius: '4px',
                                            transition: 'transform 0.2s ease-in-out',
                                            cursor: 'pointer',
                                        }}
                                        onMouseOver={e => e.currentTarget.style.transform = 'scale(1.6)'}
                                        onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                                    />
                                    <a href={tData} target="_blank" rel="noreferrer">Download</a>
                                </div>
                            );
                        } else if (label === "Thumbnail" && typeof tData === 'string' && tData !== "X") {
                            cellContent = (
                                <img
                                    src={tData}
                                    alt={accessor}
                                    style={{
                                        width: '96px',
                                        height: '54px',
                                        objectFit: 'cover',
                                        borderRadius: '4px',
                                    }}
                                />
                            );
                        } else if (label === "Video" && typeof tData === 'string' && isVideo(tData)) {
                            cellContent = (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                    <video width="120" height="80" controls style={{ borderRadius: '4px' }}>
                                        <source src={tData} type={getVideoType(tData)} />
                                        Your browser does not support the video tag.
                                    </video>
                                    <a href={tData} target="_blank" rel="noreferrer">Download</a>
                                </div>
                            );
                        } else if (label === "Active" && typeof rawValue === 'boolean') {
                            const isActive = rawValue;
                            cellContent = (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '4px 10px',
                                        borderRadius: '999px',
                                        fontWeight: 600,
                                        fontSize: '12px',
                                        letterSpacing: '0.01em',
                                        border: `1px solid ${isActive ? '#4caf50' : '#ef5350'}`,
                                        backgroundColor: isActive ? 'rgba(76, 175, 80, 0.12)' : 'rgba(239, 83, 80, 0.12)',
                                        color: isActive ? '#66bb6a' : '#e57373',
                                    }}
                                >
                                    <span
                                        aria-hidden="true"
                                        style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: isActive ? '#66bb6a' : '#e57373',
                                            display: 'inline-block',
                                        }}
                                    />
                                    {isActive ? 'Active' : 'Inactive'}
                                </span>
                            );
                        } else if (label === "CTR") {
                            cellContent = (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    {data.isHighestCtr && (
                                        <span
                                            title="Highest CTR"
                                            aria-label="Highest CTR"
                                            style={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                backgroundColor: '#f5b301',
                                                display: 'inline-block',
                                                boxShadow: '0 0 0 2px rgba(245, 179, 1, 0.2)',
                                            }}
                                        />
                                    )}
                                    <span>{tData}</span>
                                </span>
                            );
                        } else if (link && typeof tData === 'string' && tData !== "X") {
                            cellContent = <a href={tData} target="_blank" rel="noreferrer">Download</a>;
                        } else if (typeof tData === 'string' && isImage(tData)) {
                            cellContent = <img src={tData} alt={accessor} style={{ width: '50px', height: '50px', objectFit: 'cover' }} />;
                        } else if (typeof tData === 'boolean') {
                            cellContent = tData ? "True" : "False";
                        } else {
                            // ✅ Fallback: convert unknown objects to JSON string or placeholder
                            if (typeof tData === 'object') {
                                try {
                                    cellContent = JSON.stringify(tData);
                                } catch (e) {
                                    cellContent = '[Object]';
                                }
                            } else {
                                cellContent = tData;
                            }
                        }

                            return <td key={accessor}>{cellContent}</td>;
                        })}
                    </tr>
                );
            })}
        </tbody>
    );
};

export default TableBody;
